import classNames from 'classnames';
import { FormEvent, UIEvent, useLayoutEffect, useRef, useState } from 'react';
import { IChatMessage, IVisibleLobbyPlayer } from '../../../Models/LobbyModels';
import './Chat.css';

export interface IChatProps {
  players: IVisibleLobbyPlayer[];
  messages: IChatMessage[];
  playerIdHash: string;
  sendMessage: (message: string) => void;
}

const FOCUS_CLASS_NAME =
  'outline-none focus-visible:ring-3 focus-visible:ring-accent-fill';

const AT_BOTTOM_THRESHOLD = 10;

export default function Chat({
  players,
  messages,
  playerIdHash,
  sendMessage,
}: IChatProps) {
  const [messageInput, setMessageInput] = useState('');
  const [hasUnseenMessages, setHasUnseenMessages] = useState(false);
  const listRef = useRef<HTMLOListElement>(null);
  const isAtBottomRef = useRef(true);

  const displayedMessages = messages.flatMap((message) => {
    const author = players.find((p) => p.idHash === message.playerIdHash);
    return author ? [{ ...message, authorName: author.name }] : [];
  });

  const scrollToBottom = (behavior: ScrollBehavior) => {
    const list = listRef.current;
    list?.scrollTo({ top: list.scrollHeight, behavior });
  };

  useLayoutEffect(() => {
    if (isAtBottomRef.current) {
      scrollToBottom('auto');
    } else {
      setHasUnseenMessages(true);
    }
  }, [displayedMessages.length]);

  const onScroll = (e: UIEvent<HTMLOListElement>) => {
    const list = e.currentTarget;
    const isAtBottom =
      list.scrollHeight - list.clientHeight - list.scrollTop <=
      AT_BOTTOM_THRESHOLD;
    isAtBottomRef.current = isAtBottom;
    if (isAtBottom) {
      setHasUnseenMessages(false);
    }
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const message = messageInput.trim();
    if (!message) {
      return;
    }
    sendMessage(message);
    setMessageInput('');
    isAtBottomRef.current = true;
    scrollToBottom('smooth');
  };

  return (
    <section
      aria-label="Chat"
      className="flex h-full flex-col bg-surface text-ink"
    >
      <div className="relative min-h-0 flex-1">
        <ol
          ref={listRef}
          onScroll={onScroll}
          className="flex h-full flex-col gap-[4px] overflow-y-auto p-[12px] select-text"
        >
          {displayedMessages.length === 0 && (
            <li className="m-auto text-[14px] font-medium text-ink-muted">
              No messages yet
            </li>
          )}
          {displayedMessages.map((message, i) => {
            const isOwn = message.playerIdHash === playerIdHash;
            const isNewAuthor =
              message.playerIdHash !== displayedMessages[i - 1]?.playerIdHash;
            return (
              <li
                key={`${message.playerIdHash}-${String(message.sentAt)}-${i}`}
                className={classNames(
                  'flex max-w-[80%] flex-col gap-[2px]',
                  isOwn ? 'items-end self-end' : 'items-start self-start',
                  i === 0 ? 'mt-auto' : { 'mt-[8px]': isNewAuthor }
                )}
              >
                {isNewAuthor && !isOwn && (
                  <span className="px-[12px] text-[12px] font-semibold text-ink-muted">
                    {message.authorName}
                  </span>
                )}
                <span
                  className={classNames(
                    'rounded-[16px] px-[12px] py-[7px] text-[14px] font-medium whitespace-pre-wrap wrap-anywhere',
                    isOwn ? 'bg-accent-fill text-accent-on' : 'bg-control'
                  )}
                >
                  {message.message}
                </span>
              </li>
            );
          })}
        </ol>
        {hasUnseenMessages && (
          <button
            onClick={() => scrollToBottom('smooth')}
            className={classNames(
              'absolute bottom-[10px] left-1/2 h-[32px] -translate-x-1/2 rounded-full bg-straw px-[12px] text-straw-ink shadow-soft',
              FOCUS_CLASS_NAME
            )}
          >
            <span className="text-[12px] font-semibold tracking-normal normal-case">
              New messages
            </span>
          </button>
        )}
      </div>
      <form
        onSubmit={onSubmit}
        className="flex shrink-0 items-center gap-[8px] p-[10px] shadow-[inset_0_1px_0_var(--color-hairline)]"
      >
        <input
          type="text"
          aria-label="Message"
          placeholder="Message"
          autoComplete="off"
          enterKeyHint="send"
          value={messageInput}
          onChange={(e) => setMessageInput(e.target.value)}
          className="chat-input"
        />
        <button
          type="submit"
          aria-label="Send"
          disabled={!messageInput.trim()}
          className={classNames(
            'flex size-[44px] shrink-0 items-center justify-center rounded-full bg-accent-fill text-accent-on disabled:opacity-40',
            FOCUS_CLASS_NAME
          )}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M12 19V5M6 11l6-6 6 6" />
          </svg>
        </button>
      </form>
    </section>
  );
}
